import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Card } from 'react-bootstrap';
import Button from '../Button';
import Tabs from '../Tabs';
import Tab from '../Tabs/Tab';
import { ContentLabel } from '../KeyComponents';

function ActivityFeed({ children, className }) {
  return (
    <Card className={classNames('activity-feed', className)}>
      {children}
    </Card>
  );
}

ActivityFeed.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

ActivityFeed.defaultProps = {
  children: null,
  className: '',
};

function ActivityFeedHeader({ children, className }) {
  return (
    <Card.Header className={classNames('activity-feed-header', className)}>
      {children}
    </Card.Header>
  );
}

ActivityFeedHeader.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

ActivityFeedHeader.defaultProps = {
  children: null,
  className: '',
};

function ActivityFeedTitle({ children, className }) {
  return (
    <Card.Title className={classNames('activity-feed-title mb-0', className)}>
      {children}
    </Card.Title>
  );
}

ActivityFeedTitle.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

ActivityFeedTitle.defaultProps = {
  children: null,
  className: '',
};

function ActivityFeedActionButton({ children, className, onClick }) {
  return (
    <Button
      variant="link"
      className={classNames('activity-feed-action-button', className)}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

ActivityFeedActionButton.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  onClick: PropTypes.func,
};

ActivityFeedActionButton.defaultProps = {
  children: null,
  className: '',
  onClick: null,
};

function ActivityFeedTabMenu({ children, className, variant }) {
  return (
    <Tabs
      variant={variant}
      className={classNames('activity-feed-tab-menu', className)}
    >
      {children}
    </Tabs>
  );
}

ActivityFeedTabMenu.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  variant: PropTypes.string,
};

ActivityFeedTabMenu.defaultProps = {
  children: null,
  className: '',
  variant: '',
};

function ActivityFeedTab({
  children,
  className,
  eventKey,
  title,
  notification,
  separated,
}) {
  return (
    <Tab
      eventKey={eventKey}
      title={title}
      className={classNames('activity-feed-tab', className)}
      notification={notification}
      separated={separated}
    >
      {children}
    </Tab>
  );
}

ActivityFeedTab.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  eventKey: PropTypes.string,
  title: PropTypes.string,
  notification: PropTypes.node,
  separated: PropTypes.bool,
};

ActivityFeedTab.defaultProps = {
  children: null,
  className: '',
  eventKey: '',
  title: '',
  notification: null,
  separated: false,
};

function ActivityFeedBody({ children, className }) {
  return (
    <Card.Body className={classNames('activity-feed-body', className)}>
      {children}
    </Card.Body>
  );
}

ActivityFeedBody.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

ActivityFeedBody.defaultProps = {
  children: null,
  className: '',
};

function ActivityFeedDivider() {
  return <div className="activity-feed-divider" role="separator" />;
}

function ActivityFeedList({ children, className, variant }) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'ArrowDown') {
      setSelectedIndex((prevIndex) => Math.min(prevIndex + 1, React.Children.count(children) - 1));
    } else if (event.key === 'ArrowUp') {
      setSelectedIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    }
  }, [children]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div
      className={classNames('activity-feed-list', className, variant)}
    >
      {React.Children.map(children, (child, index) => React.cloneElement(child, {
        selected: index === selectedIndex,
        setIndex: setSelectedIndex,
        index,
      }))}
    </div>
  );
}

ActivityFeedList.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  variant: PropTypes.string,
};

ActivityFeedList.defaultProps = {
  children: null,
  className: '',
  variant: '',
};

function ActivityFeedItem({
  index,
  children,
  className,
  avatar,
  title,
  subtitle,
  description,
  actions,
  selected,
  onClick,
  link,
  setIndex,
  icon,
  iconType,
}) {
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    if (setIndex) {
      setIndex(index);
    }
    if (onClick) {
      onClick();
    }
    if (link) {
      navigate(link);
    }
  }, [index, onClick, link, navigate, setIndex]);

  const handleKeyDown = useCallback((event) => {
    if (selected && event.key === 'Enter') {
      handleClick();
    }
  }, [selected, handleClick]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <Card.Body
      className={classNames(
        'activity-feed-item',
        className,
        { 'activity-feed-selected': selected },
      )}
      onClick={handleClick}
    >
      <ContentLabel
        label={title}
        time={subtitle}
        description={description}
        image={avatar}
        icon={icon}
        iconType={iconType}
      >
        {children}
      </ContentLabel>
      {actions}
    </Card.Body>
  );
}

ActivityFeedItem.propTypes = {
  index: PropTypes.number,
  children: PropTypes.node,
  className: PropTypes.string,
  avatar: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  description: PropTypes.string,
  dropdown: PropTypes.node,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  link: PropTypes.string,
  setIndex: PropTypes.func,
};

ActivityFeedItem.defaultProps = {
  index: 0,
  children: null,
  className: '',
  avatar: '',
  title: '',
  subtitle: '',
  description: '',
  dropdown: null,
  selected: false,
  onClick: null,
  link: '',
  setIndex: null,
};

function ActivityFeedFooter({ className, linkHref, linkText }) {
  return (
    <Card.Footer className={classNames('activity-feed-footer', className)}>
      <div>
        Use{' '}
        <span className="mx-1 ri-arrow-up-line activity-arrow-icon" />
        <span className="mx-1 ri-arrow-down-line activity-arrow-icon" /> to
        navigate
      </div>
      {linkHref && (
        <Button
          variant="link"
          className="activity-feed-footer-action"
          href={linkHref}
        >
          <span className="ri-settings-2-line mr-1" />
          {linkText || 'Settings'}
        </Button>
      )}
    </Card.Footer>
  );
}

ActivityFeedFooter.propTypes = {
  className: PropTypes.string,
  linkHref: PropTypes.string,
  linkText: PropTypes.string,
};

ActivityFeedFooter.defaultProps = {
  className: '',
  linkHref: '',
  linkText: '',
};

ActivityFeed.Header = ActivityFeedHeader;
ActivityFeed.Title = ActivityFeedTitle;
ActivityFeed.ActionButton = ActivityFeedActionButton;
ActivityFeed.TabMenu = ActivityFeedTabMenu;
ActivityFeed.Tab = ActivityFeedTab;
ActivityFeed.Body = ActivityFeedBody;
ActivityFeed.Divider = ActivityFeedDivider;
ActivityFeed.List = ActivityFeedList;
ActivityFeed.Item = ActivityFeedItem;
ActivityFeed.Footer = ActivityFeedFooter;

export default ActivityFeed;
